import { useEffect, useState } from "react";
import { Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { UserAvatar, UserLink } from "@/components/UserBits";

const ago = (s) => new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function FeedCardControl() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/app/feed-card").then(({ data }) => setC(data)).catch(() => {}); }, []);
  const save = (body, msg) => api.put("/admin/app/feed-card", body).then(({ data }) => { setC(data); toast.success(msg); }).catch((e) => toast.error(formatErr(e)));
  if (!c) return null;
  return (
    <div className="rounded-2xl border border-rose-100 p-3 flex items-center gap-3" data-testid="admin-feed-card-control">
      <img src="/install-feed.jpg" alt="" className="w-20 h-14 rounded-xl object-cover object-right shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold">Leuchtendes Bild „App installieren“ im Feed</p>
        <p className="text-xs text-slate-500">Nur für Mitglieder ohne App sichtbar.</p>
        <button onClick={() => save({ reshow: true }, "Bild wird wieder allen ohne App angezeigt")} data-testid="admin-feed-card-reshow" className="mt-1 text-xs font-bold text-rose-600 underline">Für alle erneut einblenden</button>
      </div>
      <Switch checked={c.enabled} onCheckedChange={(v) => save({ enabled: v }, v ? "Bild im Feed eingeblendet" : "Bild im Feed ausgeblendet")} data-testid="admin-feed-card-switch" />
    </div>
  );
}

export function AppInstallStats({ a, total }) {
  const pct = total ? Math.round((a.installed / total) * 100) : 0;
  return (
    <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-4" data-testid="admin-app-installs">
      <h3 className="font-bold text-lg flex items-center gap-2"><Smartphone className="w-5 h-5 text-rose-500" /> App installiert</h3>
      <div className="flex items-end gap-3">
        <p className="font-display text-4xl font-extrabold text-rose-600" data-testid="admin-app-installed">{a.installed}</p>
        <p className="text-sm text-slate-500 pb-1">von {total} Mitgliedern ({pct} %)</p>
      </div>
      <div className="h-3 rounded-full bg-rose-50 overflow-hidden"><div className="h-full pink-gradient rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Heute", a.today, "today"], ["7 Tage", a.week, "week"], ["Mit Push", a.push_users, "push"]].map(([l, v, k]) => (
          <div key={k} className="rounded-2xl bg-rose-50/60 py-2.5" data-testid={`admin-app-${k}`}><p className="text-xl font-extrabold">{v}</p><p className="text-xs text-slate-500">{l}</p></div>
        ))}
      </div>
      {a.recent.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Zuletzt installiert</p>
          <ul className="space-y-2" data-testid="admin-app-recent">
            {a.recent.map((u) => (
              <li key={u.user_id} className="flex items-center gap-2 text-sm">
                <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-7 h-7" noStory />
                <span className="flex-1 min-w-0 truncate font-semibold"><UserLink id={u.user_id}>{u.name}</UserLink></span>
                <span className="text-xs text-slate-400">{ago(u.app_installed_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <FeedCardControl />
      <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900 space-y-2" data-testid="admin-app-reminder-info">
        <p><b>Automatische Erinnerung:</b> Mitglieder ohne App bekommen 3 Tage nach der Registrierung einmalig eine Mitteilung + Push mit dem Installier-Bonus (täglich um 19 Uhr).</p>
        <button onClick={() => api.post("/admin/install-reminder/test").then(() => toast.success("Test-Erinnerung an dich gesendet")).catch((e) => toast.error(formatErr(e)))} data-testid="admin-app-reminder-test" className="text-xs font-bold text-amber-700 underline">Test an mich senden</button>
      </div>
    </div>
  );
}
